﻿import { AdminStatusBadge } from "@/components/admin";
import { resolveAdminStatusDisplay } from "@/lib/adminCodeDisplay";
import { formatDateTime } from "@/lib/format";
import { Link } from "react-router-dom";
import type { EmergencyEventDetail } from "@/types.ts";
import type { AdminEmergencyLifecycle } from "@/lib/api";
import { AdminButton, AdminSelect, AdminTextarea } from "@/components/admin";
interface EmergencyDetailPanelProps {
    event: EmergencyEventDetail | null;
    lifecycle: AdminEmergencyLifecycle | null;
    loading: boolean;
    saving: boolean;
    onClose: () => void;
    reasonCode: string;
    reason: string;
    auditMessage: string;
    onReasonCodeChange: (value: string) => void;
    onReasonChange: (value: string) => void;
    onReopen: () => void;
}
const eventTypeLabel = (event: EmergencyEventDetail) => {
    return resolveAdminStatusDisplay({
        group: "EMERGENCY_EVENT_TYPE",
        code: event.eventType,
        name: event.eventTypeName,
        shortName: event.eventTypeShortName,
        severity: event.eventTypeSeverity,
        fallbackTone: "red",
    }).label;
};
const eventStatusDisplay = (event: EmergencyEventDetail) => {
    return resolveAdminStatusDisplay({
        group: "EMERGENCY_EVENT_STATUS",
        code: event.eventStatus,
        name: event.eventStatusName,
        shortName: event.eventStatusShortName,
        severity: event.eventSeverity,
        fallbackTone: "slate",
    });
};
const riskDisplay = (riskLevel: string) => {
    return resolveAdminStatusDisplay({
        group: "EMERGENCY_RISK_LEVEL",
        code: riskLevel,
        fallbackTone: "slate",
    });
};
const DetailRow = ({ label, value }: {
    label: string;
    value: React.ReactNode;
}) => {
    return (<div className="flex items-start justify-between py-1 border-b border-slate-100 last:border-0 gap-3">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-xs text-slate-700 font-medium text-right break-words">{value}</span>
    </div>);
};
const EmergencyDetailPanel = ({ event, lifecycle, loading, saving, reasonCode, reason, auditMessage, onReasonCodeChange, onReasonChange, onClose, onReopen, }: EmergencyDetailPanelProps) => {
    if (loading) {
        return (<div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 min-h-[16.25rem] flex items-center justify-center text-sm text-slate-500">
        이벤트 상세를 불러오는 중입니다.
      </div>);
    }
    if (!event) {
        return (<div className="bg-white rounded-lg border border-slate-200 shadow-sm p-5 flex flex-col items-center justify-center text-center min-h-[16.25rem]">
        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
          <i className="ri-alarm-warning-line text-slate-400 text-lg"/>
        </div>
        <h3 className="text-sm font-semibold text-slate-600 mb-1">긴급 이벤트를 선택하세요</h3>
        <p className="text-xs text-slate-400 max-w-[12.5rem]">목록에서 상세 버튼을 누르면 이벤트 정보가 표시됩니다.</p>
      </div>);
    }
    const canReopen = lifecycle?.availableActions.includes("REOPEN") ?? false;
    return (<div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-alarm-warning-line text-sm text-slate-500"/>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-700">긴급 이벤트 상세</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {event.elderlyName} · {eventTypeLabel(event)}
            </p>
          </div>
        </div>
        <AdminButton type="button" variant="ghost" size="icon" icon="ri-close-line" onClick={onClose} aria-label="긴급 이벤트 상세 닫기"/>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-user-line text-blue-600 text-xs"/>
            <h4 className="text-xs font-semibold text-slate-700">대상 정보</h4>
          </div>
          <div className="bg-slate-50 rounded-md p-3 space-y-1.5">
            <DetailRow label="어르신" value={event.elderlyName}/>
            <DetailRow label="Senior 계정 ID" value={event.seniorUserAccountId}/>
            <DetailRow label="기기 ID" value={event.deviceId ?? "-"}/>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-file-list-3-line text-amber-600 text-xs"/>
            <h4 className="text-xs font-semibold text-slate-700">이벤트 정보</h4>
          </div>
          <div className="bg-slate-50 rounded-md p-3 space-y-1.5">
            <DetailRow label="이벤트 ID" value={event.eventId}/>
            <DetailRow label="유형" value={eventTypeLabel(event)}/>
            <DetailRow label="위험도" value={<AdminStatusBadge label={riskDisplay(event.riskLevel).label} tone={riskDisplay(event.riskLevel).tone}/>}/>
            <DetailRow label="처리 상태" value={<AdminStatusBadge label={eventStatusDisplay(event).label} tone={eventStatusDisplay(event).tone}/>}/>
            <DetailRow label="발생 시각" value={formatDateTime(event.occurredAt)}/>
            <DetailRow label="해결 시각" value={formatDateTime(event.resolvedAt)}/>
            <DetailRow label="설명" value={event.description ?? event.eventTypeDescription ?? "-"}/>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-links-line text-emerald-600 text-xs"/>
            <h4 className="text-xs font-semibold text-slate-700">연결 정보</h4>
          </div>
          <div className="bg-slate-50 rounded-md p-3 space-y-1.5">
            <DetailRow label="상태 설명" value={event.eventStatusDescription ?? "-"}/>
            <DetailRow label="버전" value={lifecycle?.incident.versionNo ?? "-"}/>
            <DetailRow label="허용 동작" value={lifecycle?.availableActions.join(", ") || "관리자 변경 없음"}/>
            <DetailRow label="이력 완전성" value={lifecycle?.historyComplete ? "완전" : "legacy 포함"}/>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-route-line text-violet-600 text-xs"/>
            <h4 className="text-xs font-semibold text-slate-700">연계 화면</h4>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Link to={`/admin/users/seniors/${event.seniorUserAccountId}`} className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors">
              Senior 통합 상세
            </Link>
            <Link to={`/admin/location/${event.seniorUserAccountId}`} className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors">
              위치 관제
            </Link>
            <Link to={`/admin/users/seniors/${event.seniorUserAccountId}?tab=care`} className="rounded-lg border border-violet-100 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-100 transition-colors">
              생활신호
            </Link>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-slate-100 space-y-2">
        {canReopen ? <>
          <AdminSelect value={reasonCode} onValueChange={onReasonCodeChange} disabled={saving}><option value="OPERATOR_REQUEST">운영자 요청</option><option value="DATA_CORRECTION">데이터 정정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트 원복</option><option value="OTHER">기타</option></AdminSelect>
          <AdminTextarea value={reason} onChange={(event) => onReasonChange(event.target.value)} maxLength={500} rows={3} placeholder="재개 사유를 10자 이상 입력하세요." disabled={saving} />
          <AdminButton type="button" variant="danger" size="sm" disabled={saving || reason.trim().length < 10} onClick={onReopen} className="w-full">해결 사건 재개</AdminButton>
          {auditMessage && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">{auditMessage}</p>}
        </> : <p className="text-xs text-slate-500">확인·대응·해결은 연결된 Keeper의 lifecycle 권한입니다. 관리자는 현재 상태와 이력을 조회합니다.</p>}
        <div className="flex justify-end">
        <AdminButton type="button" variant="secondary" size="sm" onClick={onClose}>닫기</AdminButton>
        </div>
      </div>
    </div>);
};
export default EmergencyDetailPanel;


